import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Input,
  Label,
} from '@pantry/ui';
import { useAccountDeletionInfo, useDeleteAccount } from '@pantry/api-client';
import { useAuth } from '../../contexts/useAuth';
import { toast } from 'sonner';
import { useRouter } from '@tanstack/react-router';
import { TrashIcon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export function AccountSection() {
  const { t } = useLingui();
  const router = useRouter();
  const { signOut } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const confirmPhrase = t`delete my account`;

  const { data: deletionInfo, isLoading: isLoadingInfo } = useAccountDeletionInfo({
    enabled: isDialogOpen,
  });
  const deleteAccount = useDeleteAccount();

  const handleDeleteAccount = async () => {
    if (confirmText.toLowerCase() !== confirmPhrase.toLowerCase()) {
      toast.error(t`Please type "${confirmPhrase}" to confirm`);
      return;
    }

    try {
      await deleteAccount.mutateAsync();
      toast.success(t`Account deleted successfully`);
      await signOut();
      router.navigate({ to: '/login' });
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to delete account`;
      toast.error(message);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setConfirmText('');
  };

  const canProceed = deletionInfo?.canDelete ?? false;
  const hasHouseholdsToTransfer = (deletionInfo?.householdsRequiringTransfer?.length ?? 0) > 0;

  return (
    <Card
      className="rounded-xl border-destructive/30 bg-destructive/5"
      data-tour="settings-danger-zone"
    >
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2 text-destructive">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-destructive/10">
            <ExclamationTriangleIcon className="h-4 w-4 text-destructive" />
          </div>
          <Trans>Danger Zone</Trans>
        </CardTitle>
        <CardDescription>
          <Trans>Irreversible actions for your account.</Trans>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              <Trans>Delete Account</Trans>
            </p>
            <p className="text-sm text-muted-foreground">
              <Trans>Permanently delete your account and all associated data.</Trans>
            </p>
          </div>

          <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" className="rounded-xl">
                <TrashIcon className="h-4 w-4 mr-2" />
                <Trans>Delete Account</Trans>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-md">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display flex items-center gap-3 text-destructive">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
                    <ExclamationTriangleIcon className="h-5 w-5 text-destructive" />
                  </div>
                  <Trans>Delete Account</Trans>
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-4">
                    <p>
                      <Trans>
                        This action cannot be undone. This will permanently delete your account and
                        remove all your data from our servers.
                      </Trans>
                    </p>

                    {isLoadingInfo ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
                        <Trans>Checking account status...</Trans>
                      </div>
                    ) : hasHouseholdsToTransfer ? (
                      <div className="rounded-xl border border-destructive/50 bg-destructive/10 p-4">
                        <p className="text-sm font-medium text-destructive mb-2">
                          <Trans>Cannot delete account</Trans>
                        </p>
                        <p className="text-sm text-muted-foreground mb-2">
                          <Trans>
                            You must transfer ownership of the following households before deleting
                            your account:
                          </Trans>
                        </p>
                        <ul className="list-disc list-inside text-sm text-muted-foreground">
                          {deletionInfo?.householdsRequiringTransfer?.map((household) => (
                            <li key={household.id}>
                              {household.name} ({household.memberCount} members)
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <>
                        {(deletionInfo?.householdsToDelete?.length ?? 0) > 0 && (
                          <div className="rounded-xl border border-expiring-soon/50 bg-expiring-soon/10 p-4">
                            <p className="text-sm font-medium text-expiring-soon mb-2">
                              <Trans>The following households will be deleted:</Trans>
                            </p>
                            <ul className="list-disc list-inside text-sm text-muted-foreground">
                              {deletionInfo?.householdsToDelete?.map((household) => (
                                <li key={household.id}>{household.name}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label htmlFor="confirm-delete" className="text-sm">
                            <Trans context="form_label">Type</Trans>{' '}
                            <span className="font-mono font-semibold">{confirmPhrase}</span>{' '}
                            <Trans>to confirm</Trans>
                          </Label>
                          <Input
                            id="confirm-delete"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={confirmPhrase}
                            className="font-mono rounded-xl"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2 sm:gap-0">
                <AlertDialogCancel onClick={handleDialogClose} className="rounded-xl">
                  <Trans>Cancel</Trans>
                </AlertDialogCancel>
                {canProceed && (
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    disabled={
                      confirmText.toLowerCase() !== confirmPhrase.toLowerCase() ||
                      deleteAccount.isPending
                    }
                    className="rounded-xl bg-destructive text-white hover:bg-destructive/90"
                  >
                    {deleteAccount.isPending ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        <Trans>Deleting...</Trans>
                      </div>
                    ) : (
                      <Trans>Delete Account</Trans>
                    )}
                  </AlertDialogAction>
                )}
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}
